import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Circle } from 'lucide-react';
import { ACTIVE_VARIANTS, VARIANT_SPECS, GameVariant } from '@draughts/shared';
import { variantBoardColors } from '../game/board-themes';
import { variantPieceColors } from '../game/piece-themes';
import { buildGameRulesPath, buildRulesPath, isRulesLang, type RulesLang } from '../../seo/routes';
import './VariantsShowcase.css';

// Variants shown as full flagship cards above — excluded from the "more" chips
// so they aren't repeated.
const FEATURED_VARIANTS: GameVariant[] = [
  GameVariant.INTERNATIONAL,
  GameVariant.ITALIAN,
  GameVariant.ENGLISH,
  GameVariant.BRAZILIAN,
];

function MiniBoard({ size, variant, darkSquareBottomRight = size === 8, blackBottom = false }: { size: 8 | 10; variant: GameVariant; darkSquareBottomRight?: boolean; blackBottom?: boolean }) {
  // Per-variant colors from the MasterMatrix, applied as CSS vars that the
  // mini-cell / mini-piece rules consume (vars inherit to the descendants).
  const board = variantBoardColors(variant);
  const pieces = variantPieceColors(variant);
  const boardStyle = {
    '--mini-light': board.light,
    '--mini-dark': board.dark,
    '--mini-white': pieces.white[0]?.color,
    '--mini-white-2': pieces.white[pieces.white.length - 1]?.color,
    '--mini-black': pieces.dark[0]?.color,
    '--mini-black-2': pieces.dark[pieces.dark.length - 1]?.color,
  } as CSSProperties;
  const cells = [];
  // International 10x10: dark squares where (r+c) is odd (bottom-left is dark)
  // Italian 8x8: dark squares where (r+c) is even (bottom-right corner is dark)
  // English 8x8: same as International orientation (r+c odd)
  // blackBottom: place black pieces on the near (bottom) side — English draughts,
  // where Black moves first and sits closest to the player.
  const isDarkFn = darkSquareBottomRight
    ? (r: number, c: number) => (r + c) % 2 === 0
    : (r: number, c: number) => (r + c) % 2 === 1;
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const isDark = isDarkFn(r, c);
      const hasPiece = isDark && (r < (size === 10 ? 4 : 3) || r >= size - (size === 10 ? 4 : 3));
      const isTop = r < (size === 10 ? 4 : 3);
      const pieceColor = isTop
        ? (blackBottom ? 'white' : 'black')
        : (blackBottom ? 'black' : 'white');
      cells.push(
        <div
          key={`${r}-${c}`}
          className={`mini-cell ${isDark ? 'dark' : 'light'}`}
        >
          {hasPiece && (
            <div className={`mini-piece ${pieceColor}`} />
          )}
        </div>
      );
    }
  }

  return (
    <div className={`mini-board mini-board-${size}`} style={boardStyle}>
      {cells}
    </div>
  );
}

interface VariantsShowcaseProps {
  /** Open the rules page for a variant when its chip is clicked. */
  onSelectVariant?: (variant: GameVariant) => void;
  /** All'elenco completo delle varianti, sotto le pastiglie. */
  onOpenGameRules?: () => void;
}

export default function VariantsShowcase({ onSelectVariant, onOpenGameRules }: VariantsShowcaseProps) {
  const { t, i18n } = useTranslation();
  const lang: RulesLang = isRulesLang(i18n.language) ? i18n.language : 'en';

  return (
    <section className="variants-section">
      <div className="variants-container">
        <span className="section-label">{t('home.landing.variantsLabel')}</span>
        <h2 className="section-title">{t('home.landing.variantsTitle')}</h2>

        <div className="variants-grid">
          {/* International 10x10 */}
          <a
            className="variant-card"
            href={buildRulesPath(lang, GameVariant.INTERNATIONAL)}
            onClick={(e) => { e.preventDefault(); onSelectVariant?.(GameVariant.INTERNATIONAL); }}
          >
            <div className="variant-preview variant-preview-intl">
              <MiniBoard size={10} variant={GameVariant.INTERNATIONAL} />
            </div>
            <div className="variant-info">
              <span className="variant-badge variant-badge-intl">{t('home.landing.intlBadge')}</span>
              <h3 className="variant-name">{t('game.variants.internationalShort')}</h3>
              <p className="variant-desc">{t('home.landing.intlDesc')}</p>
              <div className="variant-specs">
                <span className="spec-pill">📐 {t('home.landing.specBoard', { size: '10×10' })}</span>
                <span className="spec-pill"><Circle size={10} fill="currentColor" /> {t('home.landing.specPieces', { count: 20 })}</span>
                <span className="spec-pill">👑 {t('home.landing.specFlyingKings')}</span>
              </div>
            </div>
          </a>

          {/* Italian 8x8 */}
          <a
            className="variant-card"
            href={buildRulesPath(lang, GameVariant.ITALIAN)}
            onClick={(e) => { e.preventDefault(); onSelectVariant?.(GameVariant.ITALIAN); }}
          >
            <div className="variant-preview variant-preview-ita">
              <MiniBoard size={8} variant={GameVariant.ITALIAN} darkSquareBottomRight={true} />
            </div>
            <div className="variant-info">
              <span className="variant-badge variant-badge-ita">{t('home.landing.italianBadge')}</span>
              <h3 className="variant-name">{t('game.variants.italianShort')}</h3>
              <p className="variant-desc">{t('home.landing.italianDesc')}</p>
              <div className="variant-specs">
                <span className="spec-pill">📐 {t('home.landing.specBoard', { size: '8×8' })}</span>
                <span className="spec-pill"><Circle size={10} fill="currentColor" /> {t('home.landing.specPieces', { count: 12 })}</span>
                <span className="spec-pill">👑 {t('home.landing.specShortKings')}</span>
              </div>
            </div>
          </a>

          {/* English 8x8 */}
          <a
            className="variant-card"
            href={buildRulesPath(lang, GameVariant.ENGLISH)}
            onClick={(e) => { e.preventDefault(); onSelectVariant?.(GameVariant.ENGLISH); }}
          >
            <div className="variant-preview variant-preview-eng">
              <MiniBoard size={8} variant={GameVariant.ENGLISH} darkSquareBottomRight={false} blackBottom />
            </div>
            <div className="variant-info">
              <span className="variant-badge variant-badge-eng">{t('home.landing.englishBadge')}</span>
              <h3 className="variant-name">{t('game.variants.englishShort')}</h3>
              <p className="variant-desc">{t('home.landing.englishDesc')}</p>
              <div className="variant-specs">
                <span className="spec-pill">📐 {t('home.landing.specBoard', { size: '8×8' })}</span>
                <span className="spec-pill"><Circle size={10} fill="currentColor" /> {t('home.landing.specPieces', { count: 12 })}</span>
                <span className="spec-pill">👑 {t('home.landing.specShortKings')}</span>
              </div>
            </div>
          </a>

          {/* Brazilian 8x8 */}
          <a
            className="variant-card"
            href={buildRulesPath(lang, GameVariant.BRAZILIAN)}
            onClick={(e) => { e.preventDefault(); onSelectVariant?.(GameVariant.BRAZILIAN); }}
          >
            <div className="variant-preview variant-preview-bra">
              <MiniBoard size={8} variant={GameVariant.BRAZILIAN} darkSquareBottomRight={false} />
            </div>
            <div className="variant-info">
              <span className="variant-badge variant-badge-bra">{t('home.landing.brazilianBadge')}</span>
              <h3 className="variant-name">{t('game.variants.brazilianShort')}</h3>
              <p className="variant-desc">{t('home.landing.brazilianDesc')}</p>
              <div className="variant-specs">
                <span className="spec-pill">📐 {t('home.landing.specBoard', { size: '8×8' })}</span>
                <span className="spec-pill"><Circle size={10} fill="currentColor" /> {t('home.landing.specPieces', { count: 12 })}</span>
                <span className="spec-pill">👑 {t('home.landing.specFlyingKings')}</span>
              </div>
            </div>
          </a>
        </div>

        <h3 className="variants-more-title">{t('home.landing.variantsMoreTitle')}</h3>
        <div className="variants-all-grid">
          {ACTIVE_VARIANTS.filter((v) => !FEATURED_VARIANTS.includes(v)).map((v) => {
            const spec = VARIANT_SPECS[v];
            const lower = v.toLowerCase();
            return (
              /*
               * Link veri, non bottoni, e per la stessa ragione gia' scritta
               * sulla griglia delle regole: queste venti pastiglie sono gli
               * unici rimandi che la landing fa alle varianti non in vetrina, e
               * finche' sono state dei <button> quelle venti pagine non hanno
               * ricevuto un solo collegamento da qui — raggiungibili, dalla
               * pagina piu' visitata del sito, solo via sitemap. Il click
               * normale resta una navigazione dentro la SPA.
               */
              <a
                key={v}
                className="variant-chip"
                href={buildRulesPath(lang, v)}
                onClick={(e) => { e.preventDefault(); onSelectVariant?.(v); }}
              >
                <span className="variant-flag" aria-hidden="true">{spec.ui.flagEmoji}</span>
                {' '}{t(`game.variants.${lower}Short`, lower)}
              </a>
            );
          })}
        </div>

        {/* E qui sotto l'elenco intero: e' la pagina che le raccoglie tutte, e
            questo e' il suo collegamento piu' forte — sta nell'HTML
            pre-renderizzato della landing, in tutte e otto le lingue. */}
        <a
          className="variants-all-cta"
          href={buildGameRulesPath(lang)}
          onClick={(e) => { e.preventDefault(); onOpenGameRules?.(); }}
        >
          {t('home.landing.variantsAllCta')} <span aria-hidden="true">→</span>
        </a>
      </div>
    </section>
  );
}
